import { mapBusinessWireToDomain } from '@/api/mappers/business';
import {
  businessesListQueryOptions,
  businessesNearbyQueryOptions,
  businessesSearchQueryOptions,
  businessesTrendingQueryOptions,
} from '@/api/queries/businesses';
import { RestaurantList } from '@/components/display/restaurant-list';
import { QueryState } from '@/components/layout/query-state';
import { ScreenHeader } from '@/components/layout/screen-header';
import { useUserLocation } from '@/hooks/use-user-location';
import { useUnits } from '@/providers/units-provider';
import { Restaurant } from '@/types/display';
import { formatDistance } from '@/utils/distance';
import { useQuery } from '@tanstack/react-query';
import React, { useMemo } from 'react';
import { View } from 'react-native';

export type BusinessListMode = 'nearby' | 'recommended' | 'cuisine' | 'trending';

export type BusinessListPageProps =
  | { mode: 'nearby'; title?: string; radiusKm?: number; limit?: number }
  | { mode: 'recommended'; title?: string; city?: string; state?: string; perPage?: number }
  | { mode: 'cuisine'; title?: string; cuisine: string; city?: string; state?: string; perPage?: number }
  | { mode: 'trending'; title?: string; limit?: number };

export function BusinessListPage(props: BusinessListPageProps) {
  const units = useUnits();
  const { coords, defaultLocation } = useUserLocation();

  const title =
    props.title ??
    (props.mode === 'nearby'
      ? 'Nearby'
      : props.mode === 'recommended'
        ? 'Recommended'
        : props.mode === 'trending'
          ? 'Trending'
          : 'Cuisine');

  const latitude = coords?.latitude ?? defaultLocation.latitude;
  const longitude = coords?.longitude ?? defaultLocation.longitude;

  const radius_km = props.mode === 'nearby' ? props.radiusKm ?? 5 : 5;
  const limit = props.mode === 'nearby' ? props.limit ?? 50 : 50;

  const recommendedCity =
    props.mode === 'recommended' ? props.city ?? defaultLocation.city : defaultLocation.city;
  const recommendedState =
    props.mode === 'recommended' ? props.state ?? defaultLocation.state : defaultLocation.state;
  const per_page =
    props.mode === 'recommended' || props.mode === 'cuisine' ? props.perPage ?? 50 : 50;

  const {
    data: nearbyData,
    isLoading: isNearbyLoading,
    error: nearbyError,
    refetch: refetchNearby,
  } = useQuery({
    ...businessesNearbyQueryOptions({
      latitude,
      longitude,
      radius_km,
      limit,
    }),
    enabled: props.mode === 'nearby' && latitude !== 0 && longitude !== 0,
  });

  const {
    data: recommendedData,
    isLoading: isRecommendedLoading,
    error: recommendedError,
    refetch: refetchRecommended,
  } = useQuery({
    ...businessesListQueryOptions({
      city: recommendedCity,
      state: recommendedState,
      per_page,
    }),
    enabled: props.mode === 'recommended',
  });

  const cuisineQuery = props.mode === 'cuisine' ? props.cuisine : '';
  const cuisineCity = props.mode === 'cuisine' ? props.city ?? defaultLocation.city : defaultLocation.city;
  const cuisineState = props.mode === 'cuisine' ? props.state ?? defaultLocation.state : defaultLocation.state;

  const {
    data: cuisineData,
    isLoading: isCuisineLoading,
    error: cuisineError,
    refetch: refetchCuisine,
  } = useQuery({
    ...businessesSearchQueryOptions({
      query: cuisineQuery,
      city: cuisineCity,
      state: cuisineState,
      per_page,
    }),
    enabled: props.mode === 'cuisine' && cuisineQuery.trim().length >= 2,
  });

  const trendingLimit = props.mode === 'trending' ? props.limit ?? 50 : 50;

  const {
    data: trendingData,
    isLoading: isTrendingLoading,
    error: trendingError,
    refetch: refetchTrending,
  } = useQuery({
    ...businessesTrendingQueryOptions({ limit: trendingLimit }),
    enabled: props.mode === 'trending',
  });

  const restaurants = useMemo((): Restaurant[] => {
    if (props.mode === 'nearby' && nearbyData) {
      return nearbyData.items.map((item) => {
        const business = mapBusinessWireToDomain(item);
        const distanceText = formatDistance(item.distance_km, units);

        return {
          id: business.id,
          name: business.name,
          cuisine: business.cuisineType || 'Restaurant',
          rating: business.rating ?? 0,
          reviews: business.totalRatings ?? 0,
          image: business.coverPhotoUrl || '',
          distance: distanceText,
          trending_dish: business.tags && business.tags.length > 0 ? business.tags[0] : undefined,
          ratingDistribution: business.ratingDistribution,
        };
      });
    }

    if (props.mode === 'recommended' && recommendedData) {
      return recommendedData.items.map((item) => {
        const business = mapBusinessWireToDomain(item);
        return {
          id: business.id,
          name: business.name,
          cuisine: business.cuisineType || 'Restaurant',
          rating: business.rating ?? 0,
          reviews: business.totalRatings ?? 0,
          image: business.coverPhotoUrl || '',
          distance: '',
          trending_dish: business.tags && business.tags.length > 0 ? business.tags[0] : undefined,
          ratingDistribution: business.ratingDistribution,
        };
      });
    }

    if (props.mode === 'cuisine' && cuisineData) {
      return cuisineData.items.map((item) => {
        const business = mapBusinessWireToDomain(item);
        return {
          id: business.id,
          name: business.name,
          cuisine: business.cuisineType || 'Restaurant',
          rating: business.rating ?? 0,
          reviews: business.totalRatings ?? 0,
          image: business.coverPhotoUrl || '',
          distance: '',
          trending_dish: business.tags && business.tags.length > 0 ? business.tags[0] : undefined,
          ratingDistribution: business.ratingDistribution,
        };
      });
    }

    if (props.mode === 'trending' && trendingData) {
      return trendingData.items.map((item) => {
        const business = mapBusinessWireToDomain(item);
        return {
          id: business.id,
          name: business.name,
          cuisine: business.cuisineType || 'Restaurant',
          rating: business.rating ?? 0,
          reviews: business.totalRatings ?? 0,
          image: business.coverPhotoUrl || '',
          distance: '',
          trending_dish: business.tags && business.tags.length > 0 ? business.tags[0] : undefined,
          ratingDistribution: business.ratingDistribution,
        };
      });
    }

    return [];
  }, [props, nearbyData, recommendedData, cuisineData, trendingData, units]);

  const isLoading =
    (props.mode === 'nearby' && isNearbyLoading) ||
    (props.mode === 'recommended' && isRecommendedLoading) ||
    (props.mode === 'cuisine' && isCuisineLoading) ||
    (props.mode === 'trending' && isTrendingLoading);

  const error =
    props.mode === 'nearby'
      ? nearbyError
      : props.mode === 'recommended'
        ? recommendedError
        : props.mode === 'trending'
          ? trendingError
          : cuisineError;

  const onRetry =
    props.mode === 'nearby'
      ? () => refetchNearby()
      : props.mode === 'recommended'
        ? () => refetchRecommended()
        : props.mode === 'trending'
          ? () => refetchTrending()
          : () => refetchCuisine();

  return (
    <View className="flex-1">
      <ScreenHeader title={title} showBackButton />

      <QueryState
        isLoading={isLoading}
        error={error}
        onRetry={onRetry}
        loadingMessage="Loading restaurants..."
        fullScreen={false}
      >
        <RestaurantList items={restaurants} />
      </QueryState>
    </View>
  );
}
